/**
 * Forgot Password Page — OTP code flow
 * Route: /forgot-password
 * Step 1: enter email -> receive a 6-digit code by email
 * Step 2: enter code + new password -> reset
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Mail, CheckCircle, AlertCircle, Loader2, KeyRound, Lock, Eye, EyeOff } from 'lucide-react';
import { SEO } from '../components/SEO';
import config from 'config';

const API = `${config.API_URL}/api`;

function formatError(detail) {
  if (!detail) return 'Une erreur est survenue. Veuillez réessayer.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1); // 1 = email, 2 = code + password, 3 = done
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const sendCode = async (e) => {
    e?.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatError(data.detail));
      setStep(2);
      setInfo('Si cette adresse email est enregistrée, un code à 6 chiffres vient d\'être envoyé.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== passwordConfirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: code.trim(), password, password_confirm: passwordConfirm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatError(data.detail));
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
      <SEO title="Mot de passe oublié" description="Réinitialisez le mot de passe de votre compte Y GO en recevant un code de vérification à 6 chiffres par email." />
      <Link to="/login" className="absolute top-6 left-6 flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] text-sm">
        <ArrowLeft className="w-4 h-4" /> Retour à la connexion
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="sr-only" data-testid="forgot-password-heading">Récupération de mot de passe Y GO</h1>
          <div className="font-['Playfair_Display'] text-4xl font-bold text-[#C9A24D] mb-2">Y GO</div>
          <p className="text-[#E5E5E5] text-sm tracking-widest uppercase">Shuttle Services France</p>
        </div>

        <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 shadow-2xl">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-xl text-[#E5E5E5]">
              {step === 3 ? 'Mot de passe réinitialisé' : 'Mot de passe oublié'}
            </CardTitle>
            <CardDescription className="text-[#E5E5E5]/60">
              {step === 1 && 'Entrez votre email pour recevoir un code de vérification'}
              {step === 2 && 'Saisissez le code reçu par email et votre nouveau mot de passe'}
              {step === 3 && ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="forgot-error">
                <AlertCircle className="w-4 h-4 shrink-0" /> {error}
              </div>
            )}

            {/* STEP 1 — email */}
            {step === 1 && (
              <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-form">
                <div>
                  <Label className="text-[#E5E5E5]">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="votre@email.fr"
                      className="pl-9 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555]"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="forgot-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer le code'}
                </Button>
              </form>
            )}

            {/* STEP 2 — code + new password */}
            {step === 2 && (
              <form onSubmit={resetPassword} className="space-y-4" data-testid="reset-otp-form">
                {info && (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-xs" data-testid="forgot-info">
                    <CheckCircle className="w-4 h-4 shrink-0" /> {info}
                  </div>
                )}
                <div>
                  <Label className="text-[#E5E5E5]">Code de vérification</Label>
                  <div className="relative mt-1">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      required
                      placeholder="123456"
                      className="pl-9 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555] tracking-[0.4em] text-center"
                      data-testid="reset-otp-code-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5]">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      placeholder="Min. 8 car., 1 maj., 1 min., 1 chiffre"
                      className="pl-9 pr-10 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555]"
                      data-testid="reset-otp-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#999]">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5]">Confirmer le mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      onPaste={(e) => e.preventDefault()}
                      required
                      minLength={8}
                      placeholder="Retapez votre mot de passe"
                      className="pl-9 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] placeholder:text-[#555]"
                      data-testid="reset-otp-password-confirm-input"
                    />
                  </div>
                </div>
                {password && passwordConfirm && password !== passwordConfirm && (
                  <p className="text-red-400 text-xs">Les mots de passe ne correspondent pas</p>
                )}
                <Button type="submit" disabled={loading || !code || !password || !passwordConfirm} className="w-full bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="reset-otp-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser le mot de passe'}
                </Button>
                <button type="button" onClick={sendCode} disabled={loading} className="w-full text-[#C9A24D] hover:text-[#d4af5a] text-xs mt-1" data-testid="reset-otp-resend">
                  Je n'ai pas reçu de code — Renvoyer
                </button>
              </form>
            )}

            {/* STEP 3 — done */}
            {step === 3 && (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-[#E5E5E5] text-sm mb-4">Votre mot de passe a été réinitialisé avec succès.</p>
                <Link to="/login">
                  <Button className="bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="go-to-login">
                    Se connecter
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
